import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Col, Row, Card, Table, Button, Dropdown, Form } from '@themesberg/react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSync, faFileExport } from '@fortawesome/free-solid-svg-icons'
import { getList, getCsv, deleteTicket } from '../../../../controller/api/ticket'
import { useCookies } from 'react-cookie'
import { TableRow } from '../component/TableRow'
import { calculatePage } from '../../../../helper/helper'
import Pagination from '../../../../components/Pagination'
import ExportProcessingPopup from '../../../../components/ExportProcessingPopup'
import { Context } from '../Ticket'
import TicketDetail from "../sub-screen/ticket-detail/TicketDetail"
import ImagePreview from '../sub-screen/image-preview/ImagePreview'
import UpdateTicketImagesModal from '../sub-screen/update-ticket-images/UpdateTicketImagesModal'
import { useLocation } from 'react-router-dom'
import { parseQuerySimple } from '../../../../helper/urlHelper'
import { ticketColumns, defaultColumns, loadTicketColumns, sortableColumns } from '../ticketView'
import GameQuickFilter from '../component/GameQuickFilter'

const _Table = () => {

  const { state, dispatch } = useContext(Context);

  const location = useLocation()
  const directDetailQuery = parseQuerySimple(location.search)
  const directDetailTicketId = directDetailQuery.showDetail === '1' ? directDetailQuery.ticketId : ''

  const [cookies] = useCookies(['lottmekong'])

  const [data, setData] = useState({})
  const [columnSettings, setColumnSettings] = useState(() => ({ scope: state.gameScope, columns: loadTicketColumns(state.gameScope) }))
  const visibleColumns = columnSettings.scope === state.gameScope ? columnSettings.columns : loadTicketColumns(state.gameScope)
  const setColumns = columns => {
    setColumnSettings({ scope: state.gameScope, columns })
    try { localStorage.setItem(`ticket-columns:${state.gameScope}`, JSON.stringify(columns)) } catch (_) { /* Storage may be unavailable. */ }
  }

  const [page, setPage] = useState(0)

  const [loading, setLoading] = useState(false)

  const [modalShow, setModalShow] = useState(false)

  const [modalImageShow, setModalImageShow] = useState(false)

  const [showExportModal, setShowExportModal] = useState(false)

  const [cancellingTicketId, setCancellingTicketId] = useState('')

  const [imageEditorTicket, setImageEditorTicket] = useState(null)

  const numberRowsRef = useRef([])

  const ticketIdRef = useRef('')

  const gameIdRef = useRef('')

  const gamePeriodIdRef = useRef('')
  
  const imagesRef = useRef([])
  
  const selectedImageRef = useRef('')
  
  const previewTitleRef = useRef('')
  
  const statusRef = useRef(0)

  const rewardRef = useRef(null)

  const reasonRef = useRef('')

  // Only the latest search request may update the table. This prevents a slow
  // previous request (for example the initial unfiltered load) from replacing
  // newer results filtered by cartId.
  const latestRequestRef = useRef(0)
  const openedDirectDetailRef = useRef('')

  useEffect(() => {
    setPage(0)
  }, [state.cartId, state.ticketId, state.gamePeriodId, state.gameId, state.gameScope, state.from, state.to, state.phone, state.customerName, state.statusGroup, state.sortBy, state.sortDirection])

  const getAllFunc = useCallback(async () => {
    const requestId = ++latestRequestRef.current

    setLoading(true)
    try {
      const response = await getList(cookies.lottmekong, {
        ticketId: state.ticketId,
        cartId: state.cartId,
        gamePeriodId: state.gamePeriodId,
        gameId: state.gameId,
        gameScope: state.gameScope || undefined,
        from: state.from,
        to: state.to,
        phone: state.phone,
        customerName: state.customerName,
        statusGroup: state.statusGroup,
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
      }, page)

      if (requestId !== latestRequestRef.current) return

      if (!state.gameScope) {
        const gameId = response?.data?.[0]?.gameId || response?.data?.[0]?.game?.id
        dispatch({ type: 'SET_GAME_SCOPE', payload: response?.gameScope || (gameId && gameId !== 'keno' ? 'other' : 'keno') })
        return
      }

      if (response) {
        setData({
          gameScope: state.gameScope,
          gameStatistics: response.gameStatistics || {},
          list: response.data || [],
          total: response.total || 0,
          totalPage: response.total ? calculatePage(response.total) : 0
        })
        dispatch({
          type: 'SET_STATUS_STATISTICS',
          payload: response.statistics
        })
      }
    } catch (error) {
      console.error('Error fetching ticket data:', error)
      if (requestId === latestRequestRef.current) {
        setData({ list: [], total: 0, totalPage: 0 })
      }
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false)
        dispatch({ type: 'SET_FILTER_LOADING', payload: false })
      }
    }
  }, [cookies.lottmekong, state.ticketId, state.cartId, state.gamePeriodId, state.gameId, state.gameScope, state.from, state.to, state.phone, state.customerName, state.statusGroup, state.sortBy, state.sortDirection, page, dispatch])

  const cancelTicketFunc = useCallback(async (ticketId) => {
    setCancellingTicketId(ticketId)

    try {
      const response = await deleteTicket(cookies.lottmekong, ticketId)

      if (response && response.status === 'success') {
        setModalShow(false)
        await getAllFunc()
        dispatch({ type: 'REFRESH_REVENUE_STATISTICS' })
        return
      }

      window.alert((response && response.message) || 'Không thể huỷ vé. Vui lòng thử lại.')
    } catch (error) {
      console.error('Error cancelling ticket:', error)
      window.alert('Không thể huỷ vé. Vui lòng thử lại.')
    } finally {
      setCancellingTicketId('')
    }
  }, [cookies.lottmekong, dispatch, getAllFunc])

  const requestCancelTicket = useCallback((ticketId) => {
    const confirmed = window.confirm(`Bạn có chắc muốn huỷ vé ${ticketId} và hoàn tiền cho người chơi?`)

    if (confirmed) {
      cancelTicketFunc(ticketId)
    }
  }, [cancelTicketFunc])

  const onShowDetailPress = useCallback((ticketId, numberRows, gameId, gamePeriodId, status, images, reward, reason) => {
    numberRowsRef.current = numberRows
    ticketIdRef.current = ticketId
    gameIdRef.current = gameId
    gamePeriodIdRef.current = gamePeriodId
    statusRef.current = status
    imagesRef.current = images
    rewardRef.current = reward
    reasonRef.current = reason || ''
    setModalShow(true)
  }, [])

  useEffect(() => {
    if (!directDetailTicketId || openedDirectDetailRef.current === directDetailTicketId) return

    const ticket = data.list && data.list.find(item => String(item.id) === String(directDetailTicketId))
    if (!ticket) return

    openedDirectDetailRef.current = directDetailTicketId
    onShowDetailPress(
      ticket.id,
      ticket.numberRows || [],
      ticket.game?.id || ticket.gameId,
      ticket.gamePeriod?.id || ticket.gamePeriodId,
      ticket.status,
      ticket.images || [],
      (ticket.TransactionPayReward || [])[0] || null,
      ticket.reason
    )
  }, [data.list, directDetailTicketId, onShowDetailPress])

  const showImagePreview = (title, image) => {
    previewTitleRef.current = title

    selectedImageRef.current = image

    setModalImageShow(true)
  }

  const handleTicketImagesUpdated = useCallback((ticketId, updatedImages) => {
    setData(current => ({
      ...current,
      list: (current.list || []).map(ticket => (
        ticket.id === ticketId ? { ...ticket, images: updatedImages } : ticket
      ))
    }))

    if (ticketIdRef.current === ticketId) {
      imagesRef.current = updatedImages
    }
  }, [])

  useEffect(() => {
    getAllFunc()
  }, [getAllFunc, state.filterSubmitCount])

  const fetchCsvPage = useCallback(
    (csvPage, dates) =>
      getCsv(cookies.lottmekong, {
        ticketId: state.ticketId,
        cartId: state.cartId,
        gamePeriodId: state.gamePeriodId,
        gameId: state.gameId,
        gameScope: state.gameScope,
        from: dates.from,
        to: dates.to,
        phone: state.phone,
        customerName: state.customerName,
        statusGroup: state.statusGroup,
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
      }, csvPage),
    [cookies.lottmekong, state.ticketId, state.cartId, state.gamePeriodId, state.gameId, state.gameScope, state.phone, state.customerName, state.statusGroup, state.sortBy, state.sortDirection]
  )

  return (
    <>
      <GameQuickFilter counts={data.gameScope === state.gameScope ? data.gameStatistics : {}} loading={loading || state.isFilterLoading || !state.gameScope} />
      <Card border="light" className="shadow-sm">
        <Card.Header className="bg-white px-3 py-2">
          <Row className="align-items-center">
            <Col>
              <h6 className="mb-0">Danh sách vé</h6>
            </Col>
            <Col xs="auto">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <small className="text-muted">{data.total || 0} vé</small>
                <Button variant="outline-primary" size="sm" disabled={loading} onClick={() => { getAllFunc(); dispatch({ type: 'REFRESH_REVENUE_STATISTICS' }) }}>
                  <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" /> Làm mới
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setShowExportModal(true)}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-1" />
                  Xuất file
                </Button>
                <Dropdown alignRight>
                  <Dropdown.Toggle variant="outline-primary" size="sm" disabled={!state.gameScope}>Tùy chỉnh cột</Dropdown.Toggle>
                  <Dropdown.Menu className="ticket-columns-menu">
                    <strong className="d-block mb-2">Tùy chỉnh cột</strong>
                    {ticketColumns.map(([id, label]) => <Form.Check key={id} id={`ticket-column-${id}`} type="checkbox"
                      className="mb-2" label={id === 'actions' ? `${label} (bắt buộc)` : label}
                      checked={visibleColumns.includes(id)} disabled={id === 'actions'}
                      onChange={event => setColumns(event.target.checked ? [...visibleColumns, id] : visibleColumns.filter(column => column !== id))} />)}
                    <Button size="sm" variant="link" className="p-0 mt-2" onClick={() => setColumns([...defaultColumns])}>Khôi phục mặc định</Button>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Table responsive className="ticket-table align-items-center table-bordered table-hover">
          <thead className='text-center'>
            <tr>
              {ticketColumns.filter(([id]) => visibleColumns.includes(id)).map(([id, label]) => <th scope="col" key={id}
                aria-sort={sortableColumns.includes(id) ? state.sortBy === id ? state.sortDirection === 'asc' ? 'ascending' : 'descending' : 'none' : undefined}>
                {sortableColumns.includes(id) ? <button type="button" className="ticket-sort-button" disabled={loading}
                  onClick={() => {
                    setPage(0);
                    dispatch({ type: 'SUBMIT_FILTER', payload: { sortBy: id, sortDirection: state.sortBy === id && state.sortDirection === 'asc' ? 'desc' : 'asc' } });
                  }}>
                  {label} <span aria-hidden="true" className={state.sortBy === id ? 'text-primary' : 'text-muted'}>{state.sortBy === id ? state.sortDirection === 'asc' ? '↑' : '↓' : '↕'}</span>
                </button> : label}
              </th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-4">
                  <FontAwesomeIcon icon={faSync} spin className="me-2" />
                  Đang tải dữ liệu vé...
                </td>
              </tr>
            ) : data.gameScope !== state.gameScope || !data.list || data.list.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Không tìm thấy vé phù hợp</td>
              </tr>
            ) : data.list.map((pv, index) => (
              <TableRow
                visibleColumns={visibleColumns}
                onShowDetailPress={onShowDetailPress}
                handleShowImagePreview={showImagePreview}
                onUpdateImagesPress={(ticketId, images) => setImageEditorTicket({ id: ticketId, images })}
                onCancelTicketPress={requestCancelTicket}
                isCancelling={cancellingTicketId === pv.id}
                rIndex={index}
                key={`table-keys-lottmekong-${pv.id}`}
                {...pv}
              />
            ))}
          </tbody>
        </Table>
        <Card.Footer className='flex-row'>
          <Row className="align-items-center">
            <Col>
              <Pagination
                totalPage={data.totalPage || 0}
                currentPage={page || 0}
                onPageChange={(i) => {
                  setPage(i)
                }}
              />
            </Col>
          </Row>
        </Card.Footer>
      </Card>
      <TicketDetail
        show={modalShow}
        ticket-id={ticketIdRef.current}
        status={statusRef.current}
        game-id={gameIdRef.current}
        rows={numberRowsRef.current}
        images={imagesRef.current}
        reward={rewardRef.current}
        reason={reasonRef.current}
        isCancelling={cancellingTicketId === ticketIdRef.current}
        handleDeleteTicket={() => requestCancelTicket(ticketIdRef.current)}
        handleShowImagePreview={showImagePreview}
        onHide={() => setModalShow(false)}
      />
      <ImagePreview
        show={modalImageShow}
        title={previewTitleRef.current}
        image={selectedImageRef.current}
        onHide={() => setModalImageShow(false)}
      />
      <UpdateTicketImagesModal
        show={Boolean(imageEditorTicket)}
        ticketId={imageEditorTicket?.id || ''}
        images={imageEditorTicket?.images || []}
        onSaved={handleTicketImagesUpdated}
        onHide={() => setImageEditorTicket(null)}
      />
      <ExportProcessingPopup
        show={showExportModal}
        defaultFrom={state.from}
        defaultTo={state.to}
        fetchPage={fetchCsvPage}
        onHide={() => setShowExportModal(false)}
      />
    </>
  );
};

export default React.memo(_Table)
